import React, { useEffect, useMemo, useState } from "react";
import { Search, Sparkles, Hammer, Star, Flame, Tag } from "lucide-react";
import { api } from "../lib/api";
import { WavyBreak, BubbleEdge } from "../components/Wavy";
import { Trolley } from "../components/supply/Trolley";
import { TrolleyDrawer } from "../components/supply/TrolleyDrawer";
import { ProductCard } from "../components/supply/ProductCard";
import { ProductModal } from "../components/supply/ProductModal";

const SORTS = [
  { value: "featured", label: "OUR PICKS" },
  { value: "price_asc", label: "PRICE: LOW → HIGH" },
  { value: "price_desc", label: "PRICE: HIGH → LOW" },
  { value: "alpha", label: "A → Z" },
];

const Section = ({ icon: Icon, title, blurb, products, onOpen, tint, delay = 0 }) => {
  if (!products.length) return null;
  return (
    <section className="max-w-[1500px] mx-auto px-5 md:px-10 py-10 animate-fade-up" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center gap-3 mb-1">
        <span className="w-10 h-10 rounded-full flex items-center justify-center border-[3px] border-plum-deep" style={{ background: tint }}>
          <Icon className="w-5 h-5 text-plum-deep" />
        </span>
        <h2 className="font-serif-display uppercase text-plum-deep text-2xl md:text-3xl tracking-[0.06em]">{title}</h2>
      </div>
      <p className="text-sm text-plum-deep/60 mb-6 ml-[52px]">{blurb}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
        {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} onOpen={onOpen} />)}
      </div>
    </section>
  );
};

export default function SupplyRoom() {
  const [data, setData] = useState(null);
  const [openProduct, setOpenProduct] = useState(null);
  const [cat, setCat] = useState("ALL");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    api.get("/supply/products").then((r) => setData(r.data)).catch(() => setData({ products: [], settings: {} }));
  }, []);

  const products = useMemo(() => data?.products || [], [data]);
  const cats = useMemo(() => ["ALL", ...Array.from(new Set(products.map((p) => p.category)))], [products]);

  const filtered = useMemo(() => {
    let list = products.filter((p) =>
      (cat === "ALL" || p.category === cat) &&
      (!search.trim() || `${p.title} ${p.description}`.toLowerCase().includes(search.trim().toLowerCase()))
    );
    if (sort === "price_asc") list = [...list].sort((a, b) => a.price_from - b.price_from);
    else if (sort === "price_desc") list = [...list].sort((a, b) => b.price_from - a.price_from);
    else if (sort === "alpha") list = [...list].sort((a, b) => a.title.localeCompare(b.title));
    return list;
  }, [products, cat, search, sort]);

  return (
    <div className="sr-drawer-enter bg-cream min-h-screen" data-testid="supply-room-page">
      {/* drawer handle flourish */}
      <div className="bg-plum relative pt-24 md:pt-28 pb-16 overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-28 h-2.5 rounded-full bg-cream/25" aria-hidden />
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 text-center relative">
          <p className="font-serif-display uppercase text-lemon text-[11px] md:text-xs tracking-[0.4em] animate-fade-up">A DELIGHTFUL LITTLE CORNER OF THE BLOOM FOUNDRY</p>
          <h1 className="font-serif-display uppercase text-cream text-4xl sm:text-5xl lg:text-6xl mt-3 animate-fade-up" style={{ animationDelay: "100ms" }}>
            THE SUPPLY ROOM
          </h1>
          <p className="font-script text-blush text-2xl md:text-3xl mt-2 animate-fade-up" style={{ animationDelay: "200ms" }}>
            Good things for growing things.
          </p>
          <p className="text-cream/70 text-sm md:text-base max-w-xl mx-auto mt-4 animate-fade-up" style={{ animationDelay: "300ms" }}>
            Useful tools, clever gadgets and interesting little finds for your garden — featuring The Etchery ORIGINALS and Bloom Foundry limited editions.
          </p>
        </div>
        <div className="absolute left-0 right-0 bottom-0"><BubbleEdge color="#FDFBF7" bump={26} /></div>
      </div>

      {data === null ? (
        <p className="text-center py-20 text-plum-deep/60 font-serif-display uppercase tracking-[0.2em]">OPENING THE DRAWER…</p>
      ) : (
        <>
          <Section icon={Star} title="THE GOOD STUFF" blurb="Featured finds we can't stop talking about." tint="#FFD23F"
            products={products.filter((p) => p.featured)} onOpen={setOpenProduct} delay={0} />
          <WavyBreak color="#4ECDE9" />
          <Section icon={Hammer} title="FRESH OFF THE BENCH" blurb="New arrivals, still warm from the workshop." tint="#C8EFF9"
            products={products.filter((p) => p.new_arrival)} onOpen={setOpenProduct} delay={100} />
          <WavyBreak color="#FF7A3D" />
          <Section icon={Flame} title="THE USUAL SUSPECTS" blurb="Best sellers that keep flying off the shelf." tint="#FFC2D1"
            products={products.filter((p) => p.bestseller)} onOpen={setOpenProduct} delay={150} />
          {products.some((p) => p.on_sale) && (
            <>
              <WavyBreak color="#1F8A50" />
              <Section icon={Tag} title="THE BARGAIN BIN" blurb="Specials — good things, better prices." tint="#D9F2E3"
                products={products.filter((p) => p.on_sale)} onOpen={setOpenProduct} delay={150} />
            </>
          )}

          <WavyBreak color="#EC2675" />

          <section className="max-w-[1500px] mx-auto px-5 md:px-10 py-10 pb-28">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 rounded-full bg-lilac flex items-center justify-center border-[3px] border-plum-deep"><Sparkles className="w-5 h-5 text-plum-deep" /></span>
              <h2 className="font-serif-display uppercase text-plum-deep text-2xl md:text-3xl tracking-[0.06em]">THE WHOLE SHELF</h2>
            </div>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-plum-deep/40" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the shelves…" data-testid="supply-search"
                  className="rounded-full pl-10 pr-5 py-2.5 bg-warmwhite border-2 border-plum-deep/15 text-sm focus:outline-none focus:border-magenta w-56" />
              </div>
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="supply-sort"
                className="rounded-full px-4 py-2.5 bg-warmwhite border-2 border-plum-deep/15 text-xs uppercase tracking-[0.1em] font-semibold focus:outline-none focus:border-magenta">
                {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <div className="flex flex-wrap gap-2">
                {cats.map((c) => (
                  <button key={c} onClick={() => setCat(c)} data-testid={`supply-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.12em] font-bold border-2 transition-all ${cat === c ? "bg-plum text-cream border-plum" : "bg-warmwhite border-plum-deep/15 text-plum-deep hover:border-magenta"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            {filtered.length === 0 ? (
              <p className="text-plum-deep/60" data-testid="supply-no-results">Nothing on that shelf — try another search!</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-testid="supply-grid">
                {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} onOpen={setOpenProduct} />)}
              </div>
            )}
          </section>
        </>
      )}

      {openProduct && <ProductModal product={openProduct} onClose={() => setOpenProduct(null)} />}
      <Trolley />
      <TrolleyDrawer settings={data?.settings} />
    </div>
  );
}
